import React, { useState, useEffect } from 'react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import { Save, Plus, Trash2, GripVertical, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';
import type { BilingualText, SiteSettings } from '@/types';

const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 mb-2";
const EMPTY_BIL: BilingualText = { en: '', ka: '' };

type PageType = 'about' | 'terms' | 'privacy' | 'cookie' | 'careers' | 'shipping';

interface Props {
  pageType: PageType;
  title: string;
}

const PAGE_CONFIG: Record<PageType, { settingsKey: string; hasIntro: boolean; hasSections: boolean; hasPositions: boolean; hasAboutFields: boolean }> = {
  about: { settingsKey: 'aboutPageContent', hasIntro: false, hasSections: false, hasPositions: false, hasAboutFields: true },
  terms: { settingsKey: 'termsContent', hasIntro: true, hasSections: true, hasPositions: false, hasAboutFields: false },
  privacy: { settingsKey: 'privacyContent', hasIntro: true, hasSections: true, hasPositions: false, hasAboutFields: false },
  cookie: { settingsKey: 'cookieContent', hasIntro: true, hasSections: true, hasPositions: false, hasAboutFields: false },
  shipping: { settingsKey: 'shippingContent', hasIntro: true, hasSections: true, hasPositions: false, hasAboutFields: false },
  careers: { settingsKey: 'careersContent', hasIntro: false, hasSections: false, hasPositions: true, hasAboutFields: false },
};

const AdminPageEditorPage: React.FC<Props> = ({ pageType, title }) => {
  const settings = useAdminStore((s) => s.settings);
  const updateSettings = useAdminStore((s) => s.updateSettings);
  const config = PAGE_CONFIG[pageType];

  // Generic sections state (terms, privacy, cookie, shipping)
  const [intro, setIntro] = useState<BilingualText>(EMPTY_BIL);
  const [sections, setSections] = useState<Array<{ title: BilingualText; text: BilingualText }>>([]);

  // About page specific
  const [apPageTitle, setApPageTitle] = useState<BilingualText>(EMPTY_BIL);
  const [apSecondParagraph, setApSecondParagraph] = useState<BilingualText>(EMPTY_BIL);
  const [apValuesLabel, setApValuesLabel] = useState<BilingualText>(EMPTY_BIL);
  const [apValuesTitle, setApValuesTitle] = useState<BilingualText>(EMPTY_BIL);
  const [apValues, setApValues] = useState<Array<{ title: BilingualText; description: BilingualText }>>([]);
  const [apCtaTitle, setApCtaTitle] = useState<BilingualText>(EMPTY_BIL);
  const [apCtaSubtitle, setApCtaSubtitle] = useState<BilingualText>(EMPTY_BIL);
  const [apCtaButton1, setApCtaButton1] = useState<BilingualText>(EMPTY_BIL);
  const [apCtaButton2, setApCtaButton2] = useState<BilingualText>(EMPTY_BIL);

  // Careers specific
  const [carPageTitle, setCarPageTitle] = useState<BilingualText>(EMPTY_BIL);
  const [carSubtitle, setCarSubtitle] = useState<BilingualText>(EMPTY_BIL);
  const [carNoJobs, setCarNoJobs] = useState<BilingualText>(EMPTY_BIL);
  const [carPositions, setCarPositions] = useState<Array<{ title: BilingualText; type: BilingualText; location: BilingualText; description: BilingualText; requirements: BilingualText }>>([]);

  // Sync from settings
  useEffect(() => {
    if (!settings) return;
    const data = (settings as unknown as Record<string, unknown>)[config.settingsKey] as Record<string, unknown> | undefined;
    if (!data) return;

    if (config.hasIntro) {
      setIntro((data.intro as BilingualText) ?? EMPTY_BIL);
    }
    if (config.hasSections) {
      setSections((data.sections as Array<{ title: BilingualText; text: BilingualText }>) ?? []);
    }
    if (config.hasAboutFields) {
      setApPageTitle((data.pageTitle as BilingualText) ?? EMPTY_BIL);
      setApSecondParagraph((data.secondParagraph as BilingualText) ?? EMPTY_BIL);
      setApValuesLabel((data.valuesLabel as BilingualText) ?? EMPTY_BIL);
      setApValuesTitle((data.valuesTitle as BilingualText) ?? EMPTY_BIL);
      setApValues((data.values as Array<{ title: BilingualText; description: BilingualText }>) ?? []);
      setApCtaTitle((data.ctaTitle as BilingualText) ?? EMPTY_BIL);
      setApCtaSubtitle((data.ctaSubtitle as BilingualText) ?? EMPTY_BIL);
      setApCtaButton1((data.ctaButton1 as BilingualText) ?? EMPTY_BIL);
      setApCtaButton2((data.ctaButton2 as BilingualText) ?? EMPTY_BIL);
    }
    if (config.hasPositions) {
      setCarPageTitle((data.pageTitle as BilingualText) ?? EMPTY_BIL);
      setCarSubtitle((data.subtitle as BilingualText) ?? EMPTY_BIL);
      setCarNoJobs((data.noJobsText as BilingualText) ?? EMPTY_BIL);
      setCarPositions((data.positions as typeof carPositions) ?? []);
    }
  }, [settings, config]);

  const [saving, setSaving] = useState(false);
  const handleSave = async (): Promise<void> => {
    if (saving) return;
    setSaving(true);
    try {
    let value: Record<string, unknown> = {};

    if (config.hasIntro && config.hasSections) {
      value = { intro, sections };
    }
    if (config.hasAboutFields) {
      value = { pageTitle: apPageTitle, secondParagraph: apSecondParagraph, valuesLabel: apValuesLabel, valuesTitle: apValuesTitle, values: apValues, ctaTitle: apCtaTitle, ctaSubtitle: apCtaSubtitle, ctaButton1: apCtaButton1, ctaButton2: apCtaButton2 };
    }
    if (config.hasPositions) {
      value = { pageTitle: carPageTitle, subtitle: carSubtitle, noJobsText: carNoJobs, positions: carPositions };
    }

    await updateSettings({ [config.settingsKey]: value } as Partial<SiteSettings>);
    toast.success('Page saved');
    } catch (err) { toast.error((err as Error).message || 'Could not save changes'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">{title}</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20">Page content editor</p>
        </div>
        <button
          disabled={saving} onClick={handleSave}
          className="flex items-center gap-2 px-5 py-3 bg-black text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Save size={14} /> Save
        </button>
      </div>

      {/* Sections-based pages (Terms, Privacy, Cookie, Shipping) */}
      {config.hasIntro && (
        <div className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-5">
          <AdminBilingualInput label="Introduction" value={intro} onChange={setIntro} textarea placeholder="Page introduction text..." rows={4} />

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className={LABEL_CLS}>Sections</label>
              <button
                onClick={() => setSections([...sections, { title: EMPTY_BIL, text: EMPTY_BIL }])}
                className="flex items-center gap-1 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider bg-black/[0.04] hover:bg-black/[0.08] rounded-lg transition-all"
              >
                <Plus size={11} /> Add Section
              </button>
            </div>
            <div className="space-y-4">
              {sections.map((section, idx) => (
                <div key={idx} className="p-5 border border-black/[0.06] rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-black/30">Section {idx + 1}</span>
                    <button onClick={() => setSections(sections.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 hover:text-red-500 hover:bg-red-500/5 transition-all">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <AdminBilingualInput label="Title" value={section.title} onChange={(v) => { const u = [...sections]; u[idx] = { ...u[idx]!, title: v }; setSections(u); }} placeholder="Section title" />
                  <AdminBilingualInput label="Content" value={section.text} onChange={(v) => { const u = [...sections]; u[idx] = { ...u[idx]!, text: v }; setSections(u); }} textarea placeholder="Section content..." rows={5} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* About Page */}
      {config.hasAboutFields && (
        <div className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-5">
          <AdminBilingualInput label="Page Title" value={apPageTitle} onChange={setApPageTitle} placeholder="e.g. About Us / ჩვენს შესახებ" />
          <AdminBilingualInput label="Second Paragraph" value={apSecondParagraph} onChange={setApSecondParagraph} textarea placeholder="Additional about text..." rows={5} />

          <div className="border-t border-black/[0.04] pt-5">
            <AdminBilingualInput label="Values Section — Label" value={apValuesLabel} onChange={setApValuesLabel} placeholder="e.g. What We Stand For" />
            <div className="mt-4"><AdminBilingualInput label="Values Section — Title" value={apValuesTitle} onChange={setApValuesTitle} placeholder="e.g. Our Values" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className={LABEL_CLS}>Value Cards</label>
              <button onClick={() => setApValues([...apValues, { title: EMPTY_BIL, description: EMPTY_BIL }])} className="flex items-center gap-1 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider bg-black/[0.04] hover:bg-black/[0.08] rounded-lg transition-all">
                <Plus size={11} /> Add Card
              </button>
            </div>
            <div className="space-y-4">
              {apValues.map((val, idx) => (
                <div key={idx} className="p-5 border border-black/[0.06] rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-black/30">Card {idx + 1}</span>
                    <button onClick={() => setApValues(apValues.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 hover:text-red-500 hover:bg-red-500/5 transition-all"><Trash2 size={13} /></button>
                  </div>
                  <AdminBilingualInput label="Title" value={val.title} onChange={(v) => { const u = [...apValues]; u[idx] = { ...u[idx]!, title: v }; setApValues(u); }} placeholder="Value title" />
                  <AdminBilingualInput label="Description" value={val.description} onChange={(v) => { const u = [...apValues]; u[idx] = { ...u[idx]!, description: v }; setApValues(u); }} textarea placeholder="Value description" rows={3} />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-black/[0.04] pt-5 space-y-4">
            <AdminBilingualInput label="CTA Title" value={apCtaTitle} onChange={setApCtaTitle} placeholder="e.g. Visit Our Gallery" />
            <AdminBilingualInput label="CTA Subtitle" value={apCtaSubtitle} onChange={setApCtaSubtitle} textarea placeholder="CTA description text..." rows={3} />
            <div className="grid grid-cols-2 gap-4">
              <AdminBilingualInput label="Button 1" value={apCtaButton1} onChange={setApCtaButton1} placeholder="e.g. Contact Us" />
              <AdminBilingualInput label="Button 2" value={apCtaButton2} onChange={setApCtaButton2} placeholder="e.g. Browse Collection" />
            </div>
          </div>
        </div>
      )}

      {/* Careers */}
      {config.hasPositions && (
        <div className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-5">
          <AdminBilingualInput label="Page Title" value={carPageTitle} onChange={setCarPageTitle} placeholder="e.g. Careers / კარიერა" />
          <AdminBilingualInput label="Subtitle" value={carSubtitle} onChange={setCarSubtitle} textarea placeholder="Page subtitle..." rows={3} />
          <AdminBilingualInput label="No Positions Text" value={carNoJobs} onChange={setCarNoJobs} textarea placeholder="Text when no open positions..." rows={3} />

          <div className="border-t border-black/[0.04] pt-5">
            <div className="flex items-center justify-between mb-4">
              <label className={LABEL_CLS}>Job Positions</label>
              <button onClick={() => setCarPositions([...carPositions, { title: EMPTY_BIL, type: EMPTY_BIL, location: EMPTY_BIL, description: EMPTY_BIL, requirements: EMPTY_BIL }])} className="flex items-center gap-1 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider bg-black/[0.04] hover:bg-black/[0.08] rounded-lg transition-all">
                <Plus size={11} /> Add Position
              </button>
            </div>
            <div className="space-y-4">
              {carPositions.map((pos, idx) => (
                <div key={idx} className="p-5 border border-black/[0.06] rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-black/30">Position {idx + 1}</span>
                    <button onClick={() => setCarPositions(carPositions.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 hover:text-red-500 hover:bg-red-500/5 transition-all"><Trash2 size={13} /></button>
                  </div>
                  <AdminBilingualInput label="Job Title" value={pos.title} onChange={(v) => { const u = [...carPositions]; u[idx] = { ...u[idx]!, title: v }; setCarPositions(u); }} placeholder="e.g. Gallery Assistant" />
                  <div className="grid grid-cols-2 gap-4">
                    <AdminBilingualInput label="Type" value={pos.type} onChange={(v) => { const u = [...carPositions]; u[idx] = { ...u[idx]!, type: v }; setCarPositions(u); }} placeholder="e.g. Full-time" />
                    <AdminBilingualInput label="Location" value={pos.location} onChange={(v) => { const u = [...carPositions]; u[idx] = { ...u[idx]!, location: v }; setCarPositions(u); }} placeholder="e.g. Tbilisi" />
                  </div>
                  <AdminBilingualInput label="Description" value={pos.description} onChange={(v) => { const u = [...carPositions]; u[idx] = { ...u[idx]!, description: v }; setCarPositions(u); }} textarea placeholder="Job description..." rows={5} />
                  <AdminBilingualInput label="Requirements" value={pos.requirements} onChange={(v) => { const u = [...carPositions]; u[idx] = { ...u[idx]!, requirements: v }; setCarPositions(u); }} textarea placeholder="Requirements..." rows={5} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPageEditorPage;
